/**
 * ForesightPassiveSkillEffect — Astral trunk passive: enemy attack-arc preview.
 *
 * When unlocked, enemy attack state machines call onEnemyWindup() before each
 * attack fires. The passive relays the notification to ForesightRenderSystem,
 * which overlays the arc on the ground so the player has time to dodge.
 *
 * Base arc duration: 400ms.
 * Foreshadow interaction (temporal_echo_upgrade_2): when TemporalEchoStateSystem
 * is recording, arc duration extends by foresightDurationBonus (200ms),
 * totaling 600ms.
 *
 * Fork 1 gate: foresight_passive is the branch point for Rift/Oracle/Dense.
 * SkillProgressionComponent handles the fork presentation — not this class.
 *
 * This SkillEffect is a pure passive adapter between enemy AI systems and
 * ForesightRenderSystem. No cooldown, no activation. Enemy systems call
 * onEnemyWindup() directly; this class routes to the render system.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § foresight_passive
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ForesightRenderSystem } from '../../../systems/ForesightRenderSystem.js';
import type { TemporalEchoStateSystem } from '../../../systems/TemporalEchoStateSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ForesightPassiveDeps {
  readonly renderer: Pick<ForesightRenderSystem, 'showArc' | 'update'>;
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly temporalEcho?: Pick<TemporalEchoStateSystem, 'isRecording'>;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ForesightPassiveConfig {
  readonly baseArcDurationMs: number;
}

const DEFAULT_CONFIG: ForesightPassiveConfig = {
  baseArcDurationMs: 400,
};

// ---------------------------------------------------------------------------
// Enemy windup descriptor
// ---------------------------------------------------------------------------

export interface EnemyWindupDescriptor {
  id: string;
  position: { x: number; y: number };
  attackDirection: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ForesightPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'foresight_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ForesightPassiveDeps;
  private readonly config: ForesightPassiveConfig;

  private _arcsTriggered = 0;
  private _lastResolvedDurationMs = 0;

  constructor(deps: ForesightPassiveDeps, config: Partial<ForesightPassiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get arcsTriggered(): number { return this._arcsTriggered; }
  get lastResolvedDurationMs(): number { return this._lastResolvedDurationMs; }

  // ---------------------------------------------------------------------------
  // Public API — called by enemy AI systems
  // ---------------------------------------------------------------------------

  /**
   * Called by an enemy's attack state machine when entering wind-up phase.
   * Routes the arc preview to ForesightRenderSystem with the resolved duration.
   *
   * Duration is extended by foresightDurationBonus (from Foreshadow) when
   * TemporalEchoStateSystem.isRecording is true.
   */
  onEnemyWindup(enemy: EnemyWindupDescriptor): void {
    const bonus = this.deps.resolver.getAdditiveEffect('foresight_passive', 'foresightDurationBonus');
    const echoActive = this.deps.temporalEcho?.isRecording ?? false;
    this._lastResolvedDurationMs = this.config.baseArcDurationMs + (echoActive ? bonus : 0);

    this.deps.renderer.showArc(
      enemy.id,
      enemy.position,
      enemy.attackDirection,
      this._lastResolvedDurationMs,
    );

    this._arcsTriggered++;
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  update(delta: number): void {
    // Tick the render system's arc expiry timers.
    this.deps.renderer.update(delta);
  }

  destroy(): void {
    // Nothing to clean up — listener-less passive.
  }
}
